// GENERATED from messaging-platform/packages/chat-core/src/patient-mentions.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
/**
 * Smart patient mentions (tasks/messaging-hub-architecture.md §8, M3): the name index over the practice's patients
 * already in memory, the `#` picker search, and detection of patient names in a message ("did you mean …?").
 *
 * Runs in the browser only, over data the browser already holds (§3.4): nothing is sent anywhere to be detected.
 * Framework-free, like the rest of chat/core.
 *
 * DETECTION, per run of words in the message (longest surname first, up to 4 words, particles kept: "van der merwe"):
 *   first name + surname          high
 *   initial + surname ("J Smith") medium
 *   title + surname ("Mrs Smith") medium
 *   first name + near surname     medium   (a typo: Jaro-Winkler >= 0.92, surnames of 5+ letters: "Johan Bohta")
 *   surname alone                 low      only when Capitalised, not a common word, and rare in the practice (<= 3)
 * A patient the user has used recently ranks first within a band. At most 3 candidates per detected name.
 *
 * NOT matched: text in `code` spans and quoted lines ("> …"), @mentions, names already #-mentioned (the caller passes
 * their spans), and "Dr <surname>" when a colleague has that surname — that is a colleague, not a patient.
 */

export interface MentionPatient {
  id: string;
  name?: string | null;
  surname?: string | null;
  dob?: string | null;
  patientNumber?: string | null;
  active?: boolean | null;
}

export type CandidateBand = 'high' | 'medium' | 'low';

export interface PatientCandidate {
  id: string;
  /** "John Smith": the chat title, and the text a # mention inserts */
  title: string;
  /** "12/03/1961 · 10042": to tell two John Smiths apart */
  detail: string;
  /** "Smith, John": how every patient list in the clinical platform names a patient. Not the mention text. */
  listTitle: string;
  /** the file number alone, for apps that show it beside the name the way their own lists do */
  number: string;
  /** the date of birth alone, formatted */
  dob: string;
  band: CandidateBand;
}

export interface Detection {
  /** the text as typed ("mrs smith") and where it is in the message */
  text: string;
  start: number;
  end: number;
  candidates: PatientCandidate[];
}

export interface PatientIndex {
  readonly size: number;
  readonly patients: ReadonlyMap<string, IndexedPatient>;
  readonly bySurname: ReadonlyMap<string, readonly IndexedPatient[]>;
  /** single-word surnames of 5+ letters by first letter, for the near-match pass */
  readonly fuzzyKeys: ReadonlyMap<string, readonly string[]>;
  readonly maxSurnameWords: number;
}

interface IndexedPatient {
  id: string;
  title: string;
  detail: string;
  listTitle: string;
  number: string;
  dob: string;
  first: string; // normalised first given name
  surname: string; // normalised surname, words joined by one space
  search: string; // normalised "given names surname patientnumber", for the # picker
}

const TITLES = new Set(['dr', 'mr', 'mrs', 'ms', 'miss', 'prof', 'sister', 'sr', 'mnr', 'mev']);
/** Titles that name a clinician: "Dr Smith" is a colleague when a colleague is called Smith. */
const CLINICIAN_TITLES = new Set(['dr', 'prof', 'sister', 'sr']);

/**
 * Surnames that are also everyday English or Afrikaans words: matched only as a full name or after a title. The design
 * doc's seed list, grown with the obvious ones; per-practice editing comes later (§8.2).
 */
export const STOP_WORDS: ReadonlySet<string> = new Set([
  'may', 'rose', 'will', 'bed', 'ward', 'baker', 'king', 'theatre',
  'april', 'august', 'june', 'mark', 'bill', 'grace', 'hope', 'joy', 'faith', 'mercy', 'patience', 'major',
  'white', 'black', 'brown', 'green', 'gray', 'grey', 'young', 'long', 'short', 'small', 'little', 'strong', 'sharp',
  'day', 'morning', 'night', 'summer', 'winter', 'spring', 'snow', 'frost', 'moon', 'star', 'sun',
  'hill', 'wood', 'woods', 'field', 'fields', 'lane', 'park', 'marsh', 'stone', 'rock', 'ridge', 'well', 'wells', 'west',
  'north', 'south', 'east', 'cross', 'church', 'hall', 'house', 'wall', 'bridge', 'ford', 'street',
  'cook', 'page', 'mason', 'hunter', 'fisher', 'carter', 'turner', 'porter', 'nurse', 'doctor', 'bishop', 'dean',
  'lord', 'knight', 'love', 'best', 'bond', 'booth', 'bell', 'ball', 'chase', 'early', 'frank', 'gold', 'silver',
  'head', 'key', 'law', 'miles', 'noble', 'post', 'power', 'price', 'reed', 'rice', 'rich', 'sage', 'sale',
  'swift', 'wild', 'wise', 'worth', 'fox', 'lamb', 'drew', 'pope', 'case', 'check', 'change', 'bank', 'banks',
  // Afrikaans everyday words that are also surnames
  'nel', 'kok', 'bester', 'smit', 'visser', 'boer', 'swart', 'wit', 'groen', 'klein', 'lange', 'jonker', 'mooi',
]);

// ── normalising ─────────────────────────────────────────────────────────────────────────

/** Lower case, accents off, apostrophes dropped, hyphens and runs of space to one space. "D'Oliveira-Smit" -> "doliveira smit". */
export function normalise(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

interface Token {
  norm: string;
  raw: string;
  start: number;
  end: number;
}

/** Words with their positions. Apostrophes stay inside a word ("O'Brien"); anything else separates. */
function tokenise(text: string): Token[] {
  const out: Token[] = [];
  const re = /[\p{L}\p{M}]+(?:['’][\p{L}\p{M}]+)*/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const norm = normalise(m[0]);
    if (norm) out.push({ norm, raw: m[0], start: m.index, end: m.index + m[0].length });
  }
  return out;
}

/** Spans detection must not look into: `code`, quoted lines, and @mentions. */
function ignoredSpans(text: string): { start: number; end: number }[] {
  const spans: { start: number; end: number }[] = [];
  for (const re of [/`[^`]*`/g, /^>.*$/gm, /@[\p{L}\p{M}.'’-]+/gu]) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) spans.push({ start: m.index, end: m.index + m[0].length });
  }
  return spans;
}

// ── the index ───────────────────────────────────────────────────────────────────────────

const two = (n: number) => String(n).padStart(2, '0');

/** "1961-03-12" or a Date string -> "12/03/1961"; '' when not a date. */
function formatDob(dob: unknown): string {
  if (!dob) return '';
  const s = String(dob);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function patientTitle(p: Pick<MentionPatient, 'name' | 'surname'>): string {
  return [p.name, p.surname].map((x) => (x == null ? '' : String(x).trim())).filter(Boolean).join(' ') || 'Patient';
}

/**
 * "Smith, John" — how the clinical platform names a patient in every list it has (the patient list, the patient
 * selection dialog, the duplicate panes). Surname first, because lists are read and sorted by surname. `patientTitle`
 * stays the other way round: it is what a `#` mention puts into the sentence, where "John Smith" is what you would say.
 */
export function listTitleOf(p: Pick<MentionPatient, 'name' | 'surname'>): string {
  const surname = (p.surname == null ? '' : String(p.surname).trim());
  const given = (p.name == null ? '' : String(p.name).trim());
  if (surname && given) return `${surname}, ${given}`;
  return surname || given || 'Patient';
}

export function buildPatientIndex(patients: readonly MentionPatient[] | null | undefined): PatientIndex {
  const byId = new Map<string, IndexedPatient>();
  const bySurname = new Map<string, IndexedPatient[]>();
  const fuzzy = new Map<string, Set<string>>();
  let maxSurnameWords = 1;
  for (const p of patients ?? []) {
    if (!p?.id || p.active === false) continue;
    const surname = normalise(p.surname);
    if (surname.length < 2) continue;
    const given = normalise(p.name);
    const entry: IndexedPatient = {
      id: String(p.id),
      title: patientTitle(p),
      detail: [formatDob(p.dob), p.patientNumber ? String(p.patientNumber).trim() : ''].filter(Boolean).join(' · '),
      listTitle: listTitleOf(p),
      number: p.patientNumber ? String(p.patientNumber).trim() : '',
      dob: formatDob(p.dob),
      first: given.split(' ')[0] ?? '',
      surname,
      search: `${given} ${surname} ${normalise(p.patientNumber)}`.trim(),
    };
    byId.set(entry.id, entry);
    bySurname.set(surname, [...(bySurname.get(surname) ?? []), entry]);
    const words = surname.split(' ').length;
    maxSurnameWords = Math.max(maxSurnameWords, Math.min(words, 4));
    if (words === 1 && surname.length >= 5) {
      const k = surname[0]!;
      fuzzy.set(k, (fuzzy.get(k) ?? new Set()).add(surname));
    }
  }
  return {
    size: byId.size,
    patients: byId,
    bySurname,
    fuzzyKeys: new Map([...fuzzy].map(([k, v]) => [k, [...v]])),
    maxSurnameWords,
  };
}

const toCandidate = (p: IndexedPatient, band: CandidateBand): PatientCandidate =>
  ({ id: p.id, title: p.title, detail: p.detail, listTitle: p.listTitle, number: p.number, dob: p.dob, band });

// ── the # picker ────────────────────────────────────────────────────────────────────────

/**
 * Patients for the `#` picker: every word typed must start a word of the patient's names or file number
 * ("jo sm" finds John Smith). Surname-first matches rank first, then recent patients, then alphabetical.
 */
export function searchPatients(index: PatientIndex, query: string, options: { limit?: number; recent?: ReadonlySet<string> } = {}): PatientCandidate[] {
  const words = normalise(query).split(' ').filter(Boolean);
  if (!words.length) return [];
  const limit = options.limit ?? 8;
  const hits: { p: IndexedPatient; rank: number }[] = [];
  for (const p of index.patients.values()) {
    const parts = p.search.split(' ');
    if (!words.every((w) => parts.some((part) => part.startsWith(w)))) continue;
    const rank = (p.surname.startsWith(words[0]!) ? 0 : 2) + (options.recent?.has(p.id) ? 0 : 1);
    hits.push({ p, rank });
  }
  return hits
    .sort((a, b) => a.rank - b.rank || a.p.title.localeCompare(b.p.title))
    .slice(0, limit)
    .map((h) => toCandidate(h.p, 'high'));
}

// ── detection ───────────────────────────────────────────────────────────────────────────

/** Jaro-Winkler similarity, 0..1 (Levenshtein is too loose for short names, §8.2). */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  const window = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const aHit = new Array<boolean>(a.length).fill(false);
  const bHit = new Array<boolean>(b.length).fill(false);
  let matches = 0;
  for (let i = 0; i < a.length; i++) {
    for (let j = Math.max(0, i - window); j < Math.min(b.length, i + window + 1); j++) {
      if (bHit[j] || a[i] !== b[j]) continue;
      aHit[i] = bHit[j] = true;
      matches++;
      break;
    }
  }
  if (!matches) return 0;
  let t = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!aHit[i]) continue;
    while (!bHit[k]) k++;
    if (a[i] !== b[k]) t++;
    k++;
  }
  const jaro = (matches / a.length + matches / b.length + (matches - t / 2) / matches) / 3;
  let prefix = 0;
  while (prefix < 4 && a[prefix] === b[prefix]) prefix++;
  return jaro + prefix * 0.1 * (1 - jaro);
}

export interface DetectOptions {
  /** spans of the message already #-mentioned (their text is not looked at again) */
  skipSpans?: readonly { start: number; end: number }[];
  /** patients never suggested: the ones already mentioned, the patient whose chat this is */
  excludeIds?: ReadonlySet<string>;
  /** patients the user has used recently: first within a band */
  recent?: ReadonlySet<string>;
  /** colleagues' surnames (normalised): "Dr <that surname>" is not a patient */
  colleagueSurnames?: ReadonlySet<string>;
}

const BAND_RANK: Record<CandidateBand, number> = { high: 0, medium: 1, low: 2 };
const MAX_CANDIDATES = 3;
const RARE = 3;

export function detectPatients(index: PatientIndex, text: string, options: DetectOptions = {}): Detection[] {
  if (!index.size || !text.trim()) return [];
  const skip = [...ignoredSpans(text), ...(options.skipSpans ?? [])];
  const inSkip = (t: Token) => skip.some((s) => t.start < s.end && t.end > s.start);
  // A skipped word also breaks a run: "#Jane Doe" must not lend "Doe" a first name.
  const tokens = tokenise(text).map((t) => (inSkip(t) ? null : t));
  const excluded = options.excludeIds ?? new Set<string>();
  const out: Detection[] = [];

  let i = 0;
  while (i < tokens.length) {
    const hit = matchAt(index, tokens, i, excluded, options);
    if (!hit) {
      i++;
      continue;
    }
    const first = tokens[hit.from]!;
    const last = tokens[hit.to]!;
    const candidates = hit.candidates
      .sort((a, b) => BAND_RANK[a.band] - BAND_RANK[b.band]
        || Number(!options.recent?.has(a.id)) - Number(!options.recent?.has(b.id))
        || a.title.localeCompare(b.title))
      .slice(0, MAX_CANDIDATES);
    if (candidates.length) out.push({ text: text.slice(first.start, last.end), start: first.start, end: last.end, candidates });
    i = hit.to + 1;
  }
  return out;
}

/** The best name starting at a surname beginning at token `i` (looking back for a first name, initial or title). */
function matchAt(
  index: PatientIndex,
  tokens: readonly (Token | null)[],
  i: number,
  excluded: ReadonlySet<string>,
  options: DetectOptions,
): { from: number; to: number; candidates: PatientCandidate[] } | null {
  if (!tokens[i]) return null;
  const before = tokens[i - 1] ?? null;
  const beforeThat = tokens[i - 2] ?? null;

  for (let len = Math.min(index.maxSurnameWords, tokens.length - i); len >= 1; len--) {
    const run = tokens.slice(i, i + len);
    if (run.some((t) => !t)) continue;
    const key = run.map((t) => t!.norm).join(' ');
    const exact = index.bySurname.get(key);
    const to = i + len - 1;
    if (exact?.length) {
      const pool = exact.filter((p) => !excluded.has(p.id));
      if (!pool.length) return null;
      const hit = score(pool, key, before, beforeThat, run[0]!, i, to, options);
      if (hit) return hit;
    } else if (len === 1 && key.length >= 5 && before && !TITLES.has(before.norm)) {
      // A near surname (a typo) counts only right after an exact first name: "Johan Bohta" finds Johan Botha.
      const near = (index.fuzzyKeys.get(key[0]!) ?? []).filter((s) => jaroWinkler(key, s) >= 0.92);
      const pool = near.flatMap((s) => index.bySurname.get(s) ?? []).filter((p) => !excluded.has(p.id) && p.first === before.norm);
      if (pool.length) return { from: i - 1, to, candidates: pool.map((p) => toCandidate(p, 'medium')) };
    }
  }
  return null;
}

function score(
  pool: readonly IndexedPatient[],
  surname: string,
  before: Token | null,
  beforeThat: Token | null,
  surnameToken: Token,
  i: number,
  to: number,
  options: DetectOptions,
): { from: number; to: number; candidates: PatientCandidate[] } | null {
  // first name + surname
  if (before) {
    const full = pool.filter((p) => p.first && p.first === before.norm);
    if (full.length) return { from: i - 1, to, candidates: full.map((p) => toCandidate(p, 'high')) };
    // initial + surname ("J Smith", "Dr J Smith")
    if (before.norm.length === 1 && /^\p{Lu}$/u.test(before.raw)) {
      const initial = pool.filter((p) => p.first.startsWith(before.norm));
      if (initial.length) {
        const from = beforeThat && TITLES.has(beforeThat.norm) ? i - 2 : i - 1;
        return { from, to, candidates: initial.map((p) => toCandidate(p, 'medium')) };
      }
    }
    // title + surname ("Mrs Smith"); "Dr Smith" is a colleague when one is called Smith
    if (TITLES.has(before.norm)) {
      if (CLINICIAN_TITLES.has(before.norm) && options.colleagueSurnames?.has(surname)) return null;
      return { from: i - 1, to, candidates: pool.map((p) => toCandidate(p, 'medium')) };
    }
  }
  // surname alone: Capitalised, not an everyday word, and rare in the practice
  const capitalised = /^\p{Lu}/u.test(surnameToken.raw);
  if (capitalised && !STOP_WORDS.has(surname) && pool.length <= RARE && surname.length >= 3) {
    return { from: i, to, candidates: pool.map((p) => toCandidate(p, 'low')) };
  }
  return null;
}
